import React, { useState, useContext, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { Link } from 'gatsby';
import { KiteButton, KiteIcon } from '@kite/react-kite';

// Context
import { AnalyticsContext } from '../../analytics/AnalyticsContext';

// Hooks
import {
  IScrollPosition,
  useScrollPosition,
} from '../../hooks/useScrollPosition/useScrollPosition';
import useBrowserStorage from '../../hooks/useBrowserStorage/useBrowserStorage';
import useAuthContext from '../../hooks/useAuthContext';

// Components
import { ButtonLink } from '../common';

// Icons
import { SearchIcon, SpectrumLogo } from '../Icons';

// Utils
import { login } from '../../utils/auth/authHelpers';

// TEALIUM
import initTealiumTracker from 'tealium-tracker';

// Styles
import './Navbar.scss';
import useAnalytics from '../../hooks/useAnalytics';

let tealiumTrackEvent;
if (typeof window !== 'undefined') {
  const { trackEvent } = initTealiumTracker({ debugMode: true });
  tealiumTrackEvent = trackEvent;
}

interface ILink {
  label: string;
  path: string;
  quantumLinkName?: string;
  partiallyActive: boolean | null;
  icon?: string;
}

const links: ILink[] = [
  {
    label: 'Home',
    path: '/',
    quantumLinkName: 'home',
    partiallyActive: false,
  },
  {
    label: 'Movies',
    path: '/movies/',
    quantumLinkName: 'movies',
    partiallyActive: true,
  },
  {
    label: 'TV Shows',
    path: '/tv/',
    quantumLinkName: 'tvShows',
    partiallyActive: null,
  },
  // {
  //   label: 'Events',
  //   path: '/events/',
  //   quantumLinkName: 'events',
  //   partiallyActive: true,
  // },
  {
    label: 'Networks',
    path: '/networks/',
    quantumLinkName: 'networks',
    partiallyActive: null,
  },
  {
    label: 'Trailers',
    path: '/trailers/',
    quantumLinkName: 'trailers',
    partiallyActive: true,
  },
  {
    label: 'TV Apps',
    path: '/tv-apps/',
    quantumLinkName: 'tvApps',
    partiallyActive: true,
  },
  {
    label: 'Access',
    path: '/access/',
    quantumLinkName: 'access',
    partiallyActive: null,
  },
];

const Navbar = ({ hasGradient = true, visitId, buyflowLink, contextToken }) => {
  const analytics = useAnalytics();
  const [scrollPosition, setScrollPosition] = useState(0);
  const [currentPath, setCurrentPath] = useState<string>('');
  const searchTrigger = useRef<HTMLAnchorElement | null>(null);
  const [navLinks, setNavLinks] = useState<ILink[]>(links);
  const [liveTVLink, setLiveTVLink] = useState('');
  const [shopLink, setShopLink] = useState('');
  const { isValidSession, logout, loginInProgress } = useAuthContext();

  const [shouldTriggerFocus, setShouldFocusTrigger] = useBrowserStorage(
    'sessionStorage',
    'shouldFocusTrigger',
    false
  );

  // helper to add active originals styling for shows
  const isOriginalsShow = currentPath.includes('spectrum-originals');

  // helper function for quantum analytics tagging
  const quantumTrack = (currPageElemStdName) => {
    analytics.track('SpectrumOnDemand_selectAction_navigationClick', {
      currPageElemStdName,
      currPageElemUiName: currPageElemStdName,
    });
  };

  useScrollPosition(
    ({ currPos }: { currPos: IScrollPosition }) => {
      setScrollPosition(currPos.y);
    },
    null,
    null,
    true,
    0
  );

  // Passes current url to preserve browser history on refresh
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = window.location.pathname || '';
      const params = window.location.search || '';
      setCurrentPath(`${url}${params}`);
    }
  }, []);

  useEffect(() => {
    // if coming from the search page, put focus back onto the search icon
    if (shouldTriggerFocus) {
      searchTrigger?.current?.focus();
    }

    return () => {
      // remove the search trigger focus flag (it will be set again on search page when needed)
      if (shouldTriggerFocus) {
        setShouldFocusTrigger(false);
      }
    };
  }, [searchTrigger, shouldTriggerFocus, setShouldFocusTrigger]);

  useEffect(() => {
    const reducedLinks = links.reduce((acc, link): ILink[] => {
      if (['Networks', 'TV Shows'].includes(link.label)) {
        const partiallyActive = window.location.pathname.includes(
          'spectrum-originals'
        )
          ? false
          : true;
        return [
          ...acc,
          {
            ...link,
            partiallyActive,
          },
        ];
      } else {
        return [...acc, link];
      }
    }, [] as ILink[]);
    setNavLinks(reducedLinks);
  }, [links]);

  // Used to test DLA authentication in lower environments
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    } else if (window.location.host.split('.')[0] !== 'ondemand') {
      const formattedContextToken = contextToken
        ? contextToken.replace('&', '?')
        : '';
      setLiveTVLink(`https://develop.ovp.spectrumtoolbox.com?env=uat`);
      setShopLink(
        `https://www.portals.uat-spectrum.net/${formattedContextToken}`
      );
    } else {
      setLiveTVLink(
        `https://watch.spectrum.net/livetv?visitId=${visitId}${contextToken}`
      );
      setShopLink(`${buyflowLink}&visitId=${visitId}${contextToken}`);
    }
  }, [visitId, buyflowLink, contextToken]);

  return (
    <nav
      className={classnames({
        nav: true,
        'nav--opaque': scrollPosition > 50 || !hasGradient,
      })}
    >
      <div className="nav__inner">
        <Link
          to="/"
          aria-label="Spectrum logo"
          onClick={() => {
            quantumTrack('home');
            tealiumTrackEvent({
              linktype: 'header',
              linkname: 'Spectrum Logo/Home',
            });
          }}
          data-linktype="header"
          data-linkname="Spectrum Logo/Home"
        >
          <SpectrumLogo />
        </Link>

        <ul className="nav__main">
          {/* Main Nav Links */}
          {navLinks.map(
            ({ path, label, icon, quantumLinkName, partiallyActive }) => (
              <li key={path}>
                <Link
                  className={classnames(
                    'link',
                    {
                      'link--originals': label === 'Originals',
                    },
                    {
                      'link--originals-show':
                        label === 'Originals' && isOriginalsShow,
                    }
                  )}
                  to={`${path}`}
                  activeClassName="link--active"
                  partiallyActive={!!partiallyActive}
                  onClick={() => {
                    quantumTrack(quantumLinkName);
                    tealiumTrackEvent({
                      linktype: 'header',
                      linkname: label,
                    });
                  }}
                  data-linktype="header"
                  data-linkname={label}
                >
                  {label}
                  {icon && <KiteIcon icon={icon} />}
                </Link>
              </li>
            )
          )}
          {/* Search Trigger */}
          <li>
            <Link
              to="/search/"
              innerRef={searchTrigger}
              title="Search"
              aria-label="Search"
              data-linktype="header"
              data-linkname="SearchIcon"
              state={{ previousPath: currentPath }}
              onClick={() => {
                quantumTrack('search');
                tealiumTrackEvent({
                  linktype: 'header',
                  linkname: 'SearchIcon',
                });
              }}
            >
              <SearchIcon />
            </Link>
          </li>
        </ul>

        <ul className="nav__actions">
          <li>
            {!isValidSession ? (
              <KiteButton
                style={{ color: '#fff' }}
                onClick={() => login(contextToken, visitId)}
                className="nav__actions--sign-in-btn"
                loading={loginInProgress}
                variant='secondary'
              >
                Sign In
              </KiteButton>
            ) : (
              <KiteButton
                style={{ color: '#fff' }}
                onClick={() => logout()}
                className="nav__actions--sign-out-btn"
                variant='secondary'
              >
                Sign Out
              </KiteButton>
            )}
          </li>
          <li>
            <ButtonLink
              type="secondary"
              path={shopLink}
              onClick={() =>
                analytics.track('SpectrumOnDemand_selectAction_shop')
              }
              tealiumConfig={{
                dataLinkName: 'Shop',
                dataLinkType: 'header',
              }}
              className="nav__actions--shop-btn"
            >
              Shop
            </ButtonLink>
          </li>
          <li>
            <a
              style={{ color: '#fff' }}
              href={liveTVLink}
              className="nav__actions--live-tv-link"
            >
              Live TV
            </a>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
